import { z } from "zod";
import { PageBlockDefinitionAppearance } from "./aux";
import { PageBlockDefinitionVariant } from "./variant";

//
// Enums
//

export const PageBlockDefinitionPropertyType = z.enum([
  "RichText",
  "MultiRichText",
  "RichTextEditor",
  "Text",
  "Boolean",
  "Number",
  "SingleSelect",
  "MultiSelect",
  "Image",
  "Token",
  "TokenType",
  "TokenProperty",
  "Component",
  "ComponentProperty",
  "Asset",
  "AssetProperty",
  "FigmaNode",
  "EmbedURL",
  "URL",
  "Markdown",
  "Code",
  "CodeSandbox",
  "Table",
  "Divider",
  "Storybook",
  "Color",
  "FigmaComponent",
  "File",
]);

export const PageBlockDefinitionRichTextPropertyStyle = z.enum([
  "Title1",
  "Title2",
  "Title3",
  "Title4",
  "Title5",
  "Quote",
  "Callout",
  "Default",
]);

export const PageBlockDefinitionMultiRichTextPropertyStyle = z.enum(["OL", "UL"]);

export const PageBlockDefinitionRichTextEditorPropertyStyle = z.enum([
  "OL",
  "UL",
  "Bold",
  "Italic",
  "Link",
  "Strikethrough",
  "InlineCode",
]);

export const PageBlockDefinitionTextPropertyStyle = z.enum([
  "Title1",
  "Title2",
  "Title3",
  "Title4",
  "Title5",
  "Default",
  "DefaultBold",
  "DefaultSemibold",
  "Small",
  "SmallBold",
  "SmallSemibold",
  "Custom",
]);

export const PageBlockDefinitionTextPropertyColor = z.enum(["Neutral", "NeutralFaded"]);

export const PageBlockDefinitionBooleanPropertyStyle = z.enum(["SegmentedControl", "ToggleButton", "Checkbox"]);
export const PageBlockDefinitionSingleSelectPropertyStyle = z.enum([
  "SegmentedControl",
  "ToggleButton",
  "Select",
  "Checkbox",
]);
export const PageBlockDefinitionSingleSelectPropertyColor = z.enum([
  "Green",
  "Red",
  "Yellow",
  "Blue",
  "Purple",
  "Orange",
  "Pink",
  "Teal",
  "Brown",
  "Grey",
  "LightGrey",
  "Cyan",
  "Fuchsia",
]);

export const IconSet = z.enum(["CheckCircle", "CrossCircle", "Alert"]);

export const PageBlockDefinitionMultiSelectPropertyStyle = z.enum(["SegmentedControl", "Select", "Checkbox"]);

export const PageBlockDefinitionImageAspectRatio = z.enum(["Auto", "Square", "Landscape", "Portrait", "Wide"]);

export const PageBlockDefinitionImageWidth = z.enum(["Full", "Icon", "Small", "Medium", "Large", "Poster"]);

export type PageBlockDefinitionPropertyType = z.infer<typeof PageBlockDefinitionPropertyType>;
export type PageBlockDefinitionRichTextPropertyStyle = z.infer<typeof PageBlockDefinitionRichTextPropertyStyle>;
export type PageBlockDefinitionMultiRichTextPropertyStyle = z.infer<
  typeof PageBlockDefinitionMultiRichTextPropertyStyle
>;
export type PageBlockDefinitionTextPropertyStyle = z.infer<typeof PageBlockDefinitionTextPropertyStyle>;
export type PageBlockDefinitionTextPropertyColor = z.infer<typeof PageBlockDefinitionTextPropertyColor>;
export type PageBlockDefinitionBooleanPropertyStyle = z.infer<typeof PageBlockDefinitionBooleanPropertyStyle>;
export type PageBlockDefinitionSingleSelectPropertyStyle = z.infer<typeof PageBlockDefinitionSingleSelectPropertyStyle>;
export type PageBlockDefinitionMultiSelectPropertyStyle = z.infer<typeof PageBlockDefinitionMultiSelectPropertyStyle>;
export type PageBlockDefinitionImageAspectRatio = z.infer<typeof PageBlockDefinitionImageAspectRatio>;
export type PageBlockDefinitionImageWidth = z.infer<typeof PageBlockDefinitionImageWidth>;

//
// Aux
//

export const PageBlockDefinitionSelectChoice = z.object({
  value: z.string(),
  name: z.string(),
  icon: IconSet.optional(),
  customIconUrl: z.string().optional(),
  color: PageBlockDefinitionSingleSelectPropertyColor.optional(),
});

export type PageBlockDefinitionSelectChoice = z.infer<typeof PageBlockDefinitionSelectChoice>;

//
// Property options
//

export const PageBlockDefinitionUntypedPropertyOptions = z.record(z.any());

export const PageBlockDefinitionRichTextOptions = z.object({
  richTextStyle: PageBlockDefinitionRichTextPropertyStyle.optional(),
});

export const PageBlockDefinitionMutiRichTextOptions = z.object({
  multiRichTextStyle: PageBlockDefinitionMultiRichTextPropertyStyle.optional(),
});

export const PageBlockDefinitionRichTextEditorOptions = z.object({
  placeholder: z.string().optional(),
  allowedInlineStyles: z.array(PageBlockDefinitionRichTextEditorPropertyStyle).optional(),
});

export const PageBlockDefinitionTextOptions = z.object({
  placeholder: z.string().optional(),
  defaultValue: z.string().optional(),
  textStyle: PageBlockDefinitionTextPropertyStyle.optional(),
  color: PageBlockDefinitionTextPropertyColor.optional(),
  allowLineBreaks: z.boolean().optional(),
});

export const PageBlockDefinitionSelectOptions = z.object({
  singleSelectStyle: PageBlockDefinitionSingleSelectPropertyStyle.optional(),
  defaultChoice: z.string(),
  choices: z.array(PageBlockDefinitionSelectChoice),
});

export const PageBlockDefinitionImageOptions = z.object({
  width: PageBlockDefinitionImageWidth.optional(),
  aspectRatio: PageBlockDefinitionImageAspectRatio.optional(),
  allowCaption: z.boolean().optional(),
  recommendation: z.string().optional(),
});

export const PageBlockDefinitionBooleanOptions = z.object({
  defaultvalue: z.boolean().optional(),
  booleanStyle: PageBlockDefinitionBooleanPropertyStyle.optional(),
});

export const PageBlockDefinitionNumberOptions = z.object({
  defaultValue: z.number(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  placeholder: z.string().optional(),
});

export const PageBlockDefinitionComponentOptions = z.object({
  renderLayoutAs: z.enum(["List", "Table"]).optional(),
  allowPropertySelection: z.boolean().optional(),
});

export const PageBlockDefinitionFigmaComponentOptions = z.object({
  renderLayoutAs: z.enum(["Grid", "PropsTable", "Canvas", "Playground"]),
  invertPropertySelection: z.boolean().optional(),
});

export type PageBlockDefinitionUntypedPropertyOptions = z.infer<typeof PageBlockDefinitionUntypedPropertyOptions>;
export type PageBlockDefinitionRichTextOptions = z.infer<typeof PageBlockDefinitionRichTextOptions>;
export type PageBlockDefinitionMutiRichTextOptions = z.infer<typeof PageBlockDefinitionMutiRichTextOptions>;
export type PageBlockDefinitionRichTextEditorOptions = z.infer<typeof PageBlockDefinitionRichTextEditorOptions>;
export type PageBlockDefinitionTextOptions = z.infer<typeof PageBlockDefinitionTextOptions>;
export type PageBlockDefinitionSelectOptions = z.infer<typeof PageBlockDefinitionSelectOptions>;
export type PageBlockDefinitionImageOptions = z.infer<typeof PageBlockDefinitionImageOptions>;
export type PageBlockDefinitionBooleanOptions = z.infer<typeof PageBlockDefinitionBooleanOptions>;
export type PageBlockDefinitionNumberOptions = z.infer<typeof PageBlockDefinitionNumberOptions>;
export type PageBlockDefinitionComponentOptions = z.infer<typeof PageBlockDefinitionComponentOptions>;
export type PageBlockDefinitionFigmaComponentOptions = z.infer<typeof PageBlockDefinitionFigmaComponentOptions>;

//
// Definitions
//

export const PageBlockDefinitionProperty = z.object({
  id: z.string(),
  name: z.string(),
  type: PageBlockDefinitionPropertyType,
  description: z.string().optional(),
  options: PageBlockDefinitionUntypedPropertyOptions.optional(),
  variantOptions: z.record(PageBlockDefinitionUntypedPropertyOptions).optional(),
});

export const PageBlockDefinitionItem = z.object({
  properties: z.array(PageBlockDefinitionProperty),
  appearance: PageBlockDefinitionAppearance.optional(),
  variants: z.array(PageBlockDefinitionVariant),
  defaultVariantKey: z.string(),
});

export type PageBlockDefinitionProperty = z.infer<typeof PageBlockDefinitionProperty>;
export type PageBlockDefinitionItem = z.infer<typeof PageBlockDefinitionItem>;
